import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/garage-floor-dc.jpg";

export const Route = createFileRoute("/resources/garage-floor-coating-maintenance")({
  head: () => ({
    meta: [
      { title: "Garage Floor Coating Maintenance Guide | MLC Epoxy Floors" },
      { name: "description", content: "How to clean, care for, and extend the life of your epoxy or polyaspartic garage floor coating. Daily, weekly, and annual care from the Calgary region's coating pros." },
      { property: "og:title", content: "Garage Floor Coating Maintenance Guide" },
      { property: "og:description", content: "How to clean, care for, and extend the life of your coated floor." },
      { property: "og:url", content: "/resources/garage-floor-coating-maintenance" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/resources/garage-floor-coating-maintenance" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(buildBreadcrumbs([
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "Maintenance Guide", href: "/resources/garage-floor-coating-maintenance" },
      ])),
    }],
  }),
  component: () => (
    <ResourceArticle
      title="Maintenance Guide"
      intro="A professionally installed polyaspartic floor needs very little to stay looking new. Follow these routines and your coating will easily last 15–20 years."
      image={heroImg}
      sections={[
        {
          type: "prose",
          heading: "The Good News",
          body: "Polyaspartic-topped floors are non-porous, chemically resistant, and UV-stable. That means no sealing, no waxing, and no special products. The bulk of maintenance is just keeping grit and standing chemicals off the surface.",
        },
        {
          type: "list",
          heading: "Daily & Weekly Care",
          items: [
            { label: "Sweep or dust mop weekly", desc: "Sand, road salt, and grit are the only real enemies — they act like sandpaper underfoot and under tires. A microfiber dust mop takes 60 seconds." },
            { label: "Wipe spills as they happen", desc: "Oil, brake fluid, gasoline, and battery acid will not damage the coating, but they get tracked everywhere. A paper towel handles it." },
            { label: "Walk-off mats at entry doors", desc: "Stops 80% of grit before it ever reaches the floor. Replace mats when they look loaded." },
          ],
        },
        {
          type: "list",
          heading: "Monthly Deep Clean",
          intro: "A quick wet clean once a month keeps the floor looking showroom-fresh.",
          items: [
            { label: "Hose or pressure-wash on low", desc: "Polyaspartic is fully waterproof — feel free to flood it." },
            { label: "Mop with warm water and a pH-neutral cleaner", desc: "Simple Green, mild dish soap, or any commercial neutral floor cleaner works. Avoid anything labeled 'degreaser' at full strength." },
            { label: "Squeegee toward the drain or door", desc: "Standing water won't damage the floor but can leave mineral spots in hard-water areas." },
          ],
        },
        {
          type: "list",
          heading: "What to Avoid",
          items: [
            { label: "Acidic cleaners", desc: "Vinegar, citrus cleaners, or anything labeled 'acid' or 'rust remover' can etch the top coat over years of repeated use." },
            { label: "Abrasive pads or steel wool", desc: "Use a soft-bristle deck brush or microfiber pad. Never scrub with steel wool or green Scotch-Brite." },
            { label: "Rubber tire mats left in place", desc: "Some rubber compounds leach plasticizers that can stain any coating. Either avoid rubber mats or move them every few weeks." },
            { label: "Hot tire pickup myth", desc: "This is a problem with cheap DIY epoxy, not professional polyaspartic systems. Park whenever you want." },
          ],
        },
        {
          type: "list",
          heading: "Annual Inspection",
          intro: "Once a year, take 5 minutes to walk the floor and check for:",
          items: [
            { label: "New cracks in the concrete", desc: "Structural cracks can telegraph through the coating. Catch them early and we can fill them before water gets underneath." },
            { label: "Edge lift at thresholds or floor drains", desc: "Rare on properly prepped floors, but easy to spot-repair if it happens." },
            { label: "Top-coat dulling in high-traffic lanes", desc: "After 10–15 years, a single refresher coat of polyaspartic restores the original gloss for a fraction of the original install cost." },
          ],
        },
        {
          type: "prose",
          heading: "When to Call Us",
          body: "If you notice any peeling, bubbling, or significant scratching from a tool drop or accident — send us a photo. Spot repairs are quick and inexpensive when caught early, and any reputable installer (us included) stands behind their work with a written warranty.",
        },
      ]}
    />
  ),
});
